import React from 'react'
import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft,
  Sparkles
} from 'lucide-react'

const MomentsGalleryPage: React.FC = () => {
  const navigate = useNavigate()
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  }

  const cardVariants = {
    hidden: { y: 25, opacity: 0, scale: 0.98 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F9F9F9' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="pt-10 pb-16"
        >
          <Link
            to="/moment-perks"
            className="inline-flex items-center space-x-2 hover:opacity-80 transition-opacity duration-300 mb-10"
            style={{ color: '#6E6D7A' }}
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>Back to Moment Perks</span>
          </Link>

          <div className="text-center">
            <motion.h1
              variants={itemVariants}
              className="text-4xl md:text-5xl font-bold mb-6"
              style={{ 
                fontFamily: 'Inter, sans-serif',
                color: '#1A2B49'
              }}
            >
              MomentPerks Gallery
            </motion.h1>
            <motion.p
              variants={itemVariants}
              className="text-lg max-w-3xl mx-auto"
              style={{ 
                fontFamily: 'Inter, sans-serif',
                color: '#6E6D7A',
                lineHeight: '1.6'
              }}
            >
              Explore ready-made MomentPerks templates to enhance your customer experience
            </motion.p>
          </div>
        </motion.div>

        {/* Gallery Content */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="pb-16"
        >
          {/* Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Order Thank You Page Card */}
            <motion.div
              variants={cardVariants}
              onClick={() => navigate('/moments-example-order-thank-you')}
              className="group cursor-pointer bg-white rounded-lg transition-all duration-300 hover:shadow-lg overflow-hidden flex flex-col"
              style={{ 
                boxShadow: '0 3px 10px rgba(0, 0, 0, 0.08)',
                borderRadius: '8px',
                minHeight: '350px' // Using minHeight instead of fixed height for responsiveness
              }}
            >
              {/* Card Header */}
              <div className="h-2" style={{ backgroundColor: '#FE4215' }}></div>
              
              {/* Card Content */}
              <div className="p-6 sm:p-8 flex flex-col flex-grow">
                <div className="flex items-center mb-4 sm:mb-6">
                  <div 
                    className="p-2 sm:p-3 rounded-lg mr-3 sm:mr-4 flex-shrink-0"
                    style={{ backgroundColor: '#FE4215' }}
                  >
                    <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <h3 
                    className="text-xl sm:text-2xl font-bold"
                    style={{ 
                      fontFamily: 'Inter, sans-serif',
                      color: '#1A2B49',
                      wordBreak: 'break-word'
                    }}
                  >
                    Order Thank You Page
                  </h3>
                </div>

                {/* Description */}
                <p 
                  className="text-sm sm:text-base mb-4 flex-grow"
                  style={{ 
                    fontFamily: 'Inter, sans-serif',
                    color: '#6E6D7A',
                    lineHeight: '1.5',
                    paddingTop: '8px',
                    wordBreak: 'break-word'
                  }}
                >
                  Enhances post-purchase experiences with personalized offers on order confirmation pages. Increases customer satisfaction and cross-sell opportunities.
                </p>

                {/* CTA Button */}
                <button
                  className="w-full py-3 sm:py-4 px-4 sm:px-6 rounded-lg font-bold text-white transition-all duration-300 hover:opacity-90 mt-auto"
                  style={{ 
                    backgroundColor: '#FE4215',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '16px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 5px rgba(254, 66, 21, 0.2)',
                    border: 'none',
                    marginTop: '16px',
                    whiteSpace: 'normal',
                    height: 'auto',
                    minHeight: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  Preview Template
                </button>
              </div>
            </motion.div>

            {/* Customer Support Demo Card */}
            <motion.div
              variants={cardVariants}
              onClick={() => navigate('/customer-support')}
              className="group cursor-pointer bg-white rounded-lg transition-all duration-300 hover:shadow-lg overflow-hidden flex flex-col"
              style={{ 
                boxShadow: '0 3px 10px rgba(0, 0, 0, 0.08)',
                borderRadius: '8px',
                minHeight: '350px' // Using minHeight instead of fixed height for responsiveness
              }}
            >
              {/* Card Header */}
              <div className="h-2" style={{ backgroundColor: '#FE4215' }}></div>
              
              {/* Card Content */}
              <div className="p-6 sm:p-8 flex flex-col flex-grow">
                <div className="flex items-center mb-4 sm:mb-6">
                  <div 
                    className="p-2 sm:p-3 rounded-lg mr-3 sm:mr-4 flex-shrink-0"
                    style={{ backgroundColor: '#FE4215' }}
                  >
                    <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <h3 
                    className="text-xl sm:text-2xl font-bold"
                    style={{ 
                      fontFamily: 'Inter, sans-serif',
                      color: '#1A2B49',
                      wordBreak: 'break-word'
                    }}
                  >
                    Customer Support Demo
                  </h3>
                </div>

                {/* Description */}
                <p 
                  className="text-sm sm:text-base mb-4 flex-grow"
                  style={{ 
                    fontFamily: 'Inter, sans-serif',
                    color: '#6E6D7A',
                    lineHeight: '1.5',
                    paddingTop: '8px',
                    wordBreak: 'break-word'
                  }}
                >
                  Transforms waiting time into engagement opportunities. Showcases real-time API integration delivering relevant offers during support interactions.
                </p>

                {/* CTA Button */}
                <button
                  className="w-full py-3 sm:py-4 px-4 sm:px-6 rounded-lg font-bold text-white transition-all duration-300 hover:opacity-90 mt-auto"
                  style={{ 
                    backgroundColor: '#FE4215',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '16px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 5px rgba(254, 66, 21, 0.2)',
                    border: 'none',
                    marginTop: '16px',
                    whiteSpace: 'normal',
                    height: 'auto',
                    minHeight: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  Try Support Demo
                </button>
              </div>
            </motion.div>

            {/* Modal Offers Demo Card */}
            <motion.div
              variants={cardVariants}
              onClick={() => navigate('/modal-offers-demo')}
              className="group cursor-pointer bg-white rounded-lg transition-all duration-300 hover:shadow-lg overflow-hidden flex flex-col"
              style={{ 
                boxShadow: '0 3px 10px rgba(0, 0, 0, 0.08)',
                borderRadius: '8px',
                minHeight: '350px' // Using minHeight instead of fixed height for responsiveness
              }}
            >
              {/* Card Header */}
              <div className="h-2" style={{ backgroundColor: '#FE4215' }}></div>
              
              {/* Card Content */}
              <div className="p-6 sm:p-8 flex flex-col flex-grow">
                <div className="flex items-center mb-4 sm:mb-6">
                  <div 
                    className="p-2 sm:p-3 rounded-lg mr-3 sm:mr-4 flex-shrink-0"
                    style={{ backgroundColor: '#FE4215' }}
                  >
                    <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <h3 
                    className="text-xl sm:text-2xl font-bold"
                    style={{ 
                      fontFamily: 'Inter, sans-serif',
                      color: '#1A2B49',
                      wordBreak: 'break-word'
                    }}
                  >
                    Modal Offers Demo
                  </h3>
                </div>

                {/* Description */}
                <p 
                  className="text-sm sm:text-base mb-4 flex-grow"
                  style={{ 
                    fontFamily: 'Inter, sans-serif',
                    color: '#6E6D7A',
                    lineHeight: '1.5',
                    paddingTop: '8px',
                    wordBreak: 'break-word'
                  }}
                >
                  Showcases modal implementation that triggers personalized offers after user actions like adding products to cart. Creates seamless shopping experiences.
                </p>

                {/* CTA Button */}
                <button
                  className="w-full py-3 sm:py-4 px-4 sm:px-6 rounded-lg font-bold text-white transition-all duration-300 hover:opacity-90 mt-auto"
                  style={{ 
                    backgroundColor: '#FE4215',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '16px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 5px rgba(254, 66, 21, 0.2)',
                    border: 'none',
                    marginTop: '16px',
                    whiteSpace: 'normal',
                    height: 'auto',
                    minHeight: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  Try Modal Demo
                </button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default MomentsGalleryPage
